import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { BigQueryPage, UserListEntity } from '../../../types';
import { useNavigate } from 'react-router';
import UsersIndexedGridToolbar from './users-indexed-grid-toolbar';
import { bigQueryFilterOperators } from '../../shared/big-query-filter-operators';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../utils/use-user-settings';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { GroupsFilter } from './filter-components/groups-filter';
import GroupChipsCell from '../../shared/group-chips-cell';
import { pagesConfig } from '../../shared/pages-config';
import useGridColumns from '../../shared/hooks/use-grid-columns-latest';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getIndexedUsers, UserKeys } from '../../api/users-api';
import { Messages } from '../../utils/message-constants';
import { isMissingBigQueryTableError } from '../../utils/error-utils';
import UserAvatar from '../../shared/user-avatar';
import SharedDataGridServer from '../../shared/shared-data-grid-server';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import QuickReportsDialog from '../../shared/quick-reports-dialog';
import { quickReportsConfig } from '../../shared/quick-reports-config';
import * as analytics from '../../shared/analytics/analytics.json';
import StatusLabel from '../../shared/status-label';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Person from '@mui/icons-material/Person';
interface UsersIndexedGridProps {
  userRoles: string[];
  /**
   * A filter to apply on mount instead of the user's persisted saved-view filter (eg. arriving
   * here via the Active Users dashboard tile, which navigates in already filtered to Status =
   * Active). Takes precedence over `initialState`'s persisted filter since it reflects an
   * explicit ask from wherever the user came from, not a leftover default.
   */
  presetFilterModel?: GridFilterModel;
}

export default function UsersIndexedGrid({
  userRoles,
  presetFilterModel,
}: UsersIndexedGridProps) {
  const navigate = useNavigate();
  const { currentTier, changeLogRoles, viewType, features } = useUserSettings();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openQuickReports, setOpenQuickReports] = useState(false);
  const groupsGovernanceEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );

  const columns: GridColDef<UserListEntity>[] = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: 'User Email',
        flex: 2.5,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.userEmail}
                sx={{ width: 36, height: 36 }}
              />
              <Typography variant="body2">{params.row.userEmail}</Typography>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        editable: false,
        sortable: true,
        filterable: true,
        flex: 1,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        editable: false,
        sortable: true,
        filterable: true,
        flex: 1,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        filterable: true,
        flex: 1,
        type: 'singleSelect',
        filterOperators: getGridSingleSelectOperators()
          .filter((op) => op.value === 'is' || op.value === 'not')
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueOptions: [
          { label: 'active', value: 'Active' },
          { label: 'suspended', value: 'Suspended' },
          { label: 'archived', value: 'Archived' },
        ],
        renderCell: (params) => {
          const status = params.row.archived
            ? 'ARCHIVED'
            : params.row.suspended
            ? 'SUSPENDED'
            : 'ACTIVE';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'orgUnitName',
        headerName: 'Org Unit Name',
        flex: 2,
        editable: false,
        sortable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
      {
        field: 'orgUnitPath',
        headerName: 'Org Unit Path',
        flex: 2,
        editable: false,
        sortable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (_value, row) => {
          // If orgUnitPath is not set then it is the root, return '/' as the value
          return row.orgUnitPath && row.orgUnitPath.trim() !== ''
            ? row.orgUnitPath
            : 'Not Indexed';
        },
      },
      ...(groupsGovernanceEnabled
        ? [
            {
              field: 'groupEmails',
              headerName: 'Groups',
              flex: 2,
              editable: false,
              sortable: false,
              filterable: true,
              display: 'flex' as const,
              renderCell: (params: { row: UserListEntity }) => (
                <GroupChipsCell groupEmails={params.row.groupEmails} />
              ),
              filterOperators: getGridStringOperators()
                .filter((op) => op.value === 'isAnyOf')
                .map((operator) => {
                  return {
                    ...operator,
                    InputComponent: GroupsFilter,
                  };
                }),
            },
          ]
        : []),
      {
        field: 'accountCreatedTime',
        headerName: 'Created',
        type: 'dateTime',
        valueGetter: (_value, row) => {
          if (row.accountCreatedTime === 0) return '';
          return new Date(row.accountCreatedTime);
        },
        valueFormatter: (value) => {
          if (value === '') return '';
          return new Date(value as string).toLocaleDateString();
        },
        minWidth: 200,
        editable: false,
        sortable: true,
        filterable: true,
        filterOperators: getGridDateOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedDateOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
      },
    ],
    [groupsGovernanceEnabled]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'userManagementIndexedGridStates'
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 25,
    page: initialState?.pagination?.paginationModel?.page || 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>(
    initialState?.sorting?.sortModel &&
      initialState.sorting.sortModel.length > 0
      ? initialState.sorting.sortModel
      : [{ field: 'userEmail', sort: 'asc' }]
  );

  const [gridSortModel, setGridSortModel] = useState<GridSortModel>(
    initialState?.sorting?.sortModel &&
      initialState.sorting.sortModel.length > 0
      ? initialState.sorting.sortModel
      : [{ field: 'userEmail', sort: 'asc' }]
  );

  const defaultFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterLogicOperator: GridLogicOperator.And,
    quickFilterValues: [],
  };

  const initialGridFilterModel =
    presetFilterModel ||
    initialState?.filter?.filterModel ||
    defaultFilterModel;

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    const model = initialGridFilterModel;
    const quickFilterValue = model.quickFilterValues?.join(' ') ?? '';

    if (quickFilterValue) {
      const items = [...(model.items || [])];
      const userEmailFilterIndex = items.findIndex(
        (item) => item.field === 'userEmail'
      );

      if (userEmailFilterIndex > -1) {
        items[userEmailFilterIndex].value = quickFilterValue;
      } else {
        items.push({
          field: 'userEmail',
          operator: 'contains',
          value: quickFilterValue,
        });
      }
      return { ...model, items };
    }
    return model;
  });

  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(
    initialGridFilterModel
  );

  const onSortChange = useCallback((newSortModel: GridSortModel) => {
    setGridSortModel(newSortModel);
    setSortModel(
      newSortModel.length > 0
        ? newSortModel
        : [{ field: 'userEmail', sort: 'asc' }]
    );
  }, []);

  const onFilterChange = useCallback(
    (gridFilterModel: GridFilterModel) => {
      const updateItems = (item: GridFilterItem) => {
        if (item.field === 'accountCreatedTime' && item.value) {
          if (item.operator === 'after' || item.operator === 'onOrBefore') {
            const timeString = new Date(item.value);
            timeString.setUTCHours(23, 59, 59, 999);
            const endOfDay = timeString.getTime().toString();
            return { ...item, field: item.field, value: endOfDay };
          } else {
            const timeString = new Date(item.value).getTime().toString();
            return { ...item, field: item.field, value: timeString };
          }
        } else {
          return item;
        }
      };

      const removedGridItems = gridFilterModel.items.filter(
        (item) => item.value
      );
      const quickFilterValue = gridFilterModel.quickFilterValues?.[0];

      const updatedItems = removedGridItems.map((item) => {
        if (quickFilterValue && quickFilterValue.length > 0) {
          if (item.field === 'userEmail') {
            return { ...item, value: quickFilterValue };
          }
          return updateItems(item);
        }
        return updateItems(item);
      });

      if (
        quickFilterValue &&
        !gridFilterModel.items.some((item) => item.field === 'userEmail')
      ) {
        updatedItems.push({
          field: 'userEmail',
          operator: 'contains',
          value: quickFilterValue,
        });
      }

      const updatedFilterModel = {
        ...gridFilterModel,
        items: updatedItems,
      };

      if (JSON.stringify(updatedFilterModel) !== JSON.stringify(filterModel)) {
        setFilterModel(updatedFilterModel);
      }

      setGridFilterModel(gridFilterModel);
    },
    [filterModel]
  );

  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: [
          {
            field: 'userEmail',
            operator: 'contains',
          },
        ],
      },
    },
    columns: {
      columnVisibilityModel: {
        orgUnitPath: false,
      },
    },
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      UserKeys.UsersIndexedPage,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
      sortModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<UserListEntity[]> = {
        results: [],
        pageSize: paginationModel.pageSize,
        currentPage: paginationModel.page,
        totalCount: 0,
      };
      return getIndexedUsers(object, filterModel, sortModel);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('indexed users'),
    },
  });

  const overlays = isMissingBigQueryTableError(error)
    ? createGridOverlays(
        'Users are currently being indexed',
        <Person />,
        'The grid will refresh once complete.',
        'The grid will refresh once complete.',
        true
      )
    : createGridOverlays(
        'Users',
        <Person />,
        'Try updating your search or filters.'
      );

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      filterModel,
      setFilterModel,
      gridFilterModel,
      setGridFilterModel,
      sortModel,
      userRoles,
      selectedRows,
      setSelectedRows,
      changeLogRoles,
      currentTier,
      setOpenQuickReports,
    }),
    [
      changeLogRoles,
      currentTier,
      filterModel,
      gridFilterModel,
      paginationModel,
      selectedRows,
      sortModel,
      userRoles,
    ]
  );

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: useToolbar(UsersIndexedGridToolbar, toolbarProps),
          ...overlays,
        }}
        initialState={initialState || defaultGridState}
        filterOperator={[GridLogicOperator.And]}
        checkboxSelection={currentTier !== 'essentials'}
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        getRowId={(row) => row.userEmail}
        data={data}
        columns={computedColumns}
        paginationModel={paginationModel}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onPaginationModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
            });
          } else {
            setPaginationModel({
              ...newModel,
            });
          }
        }}
        sortModel={gridSortModel}
        onSortModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          onSortChange(newModel);
        }}
        filterModel={gridFilterModel}
        onFilterModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          onFilterChange(newModel);
        }}
        onRowClick={(row: GridRowParams<UserListEntity>) => {
          navigate(`/${pagesConfig.users.link}/${row.row.userEmail}`);
        }}
        loading={isLoading || isFetching}
        gridKey={viewType.userManagementIndexedGridStates?.activeGridState}
      />
      <QuickReportsDialog
        open={openQuickReports}
        handleClose={() => setOpenQuickReports(false)}
        sourceData={selectedRows}
        sourceType="User"
        dialogTitle={`User Management - ${selectedRows.length} Selected`}
        analyticsEvent={analytics.user_management.user_grid.quick_report}
        contentDescription={`Generate a one-time report for the selected user${
          selectedRows.length > 1 ? 's' : ''
        }`}
        reports={quickReportsConfig}
        changeLogRoles={changeLogRoles}
      />
    </>
  );
}
